import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Variables from '../../design-system/variables';
import AddIcon from '@mui/icons-material/Add';
import JSONInput from 'react-json-editor-ajrm';
// @ts-ignore
import locale from 'react-json-editor-ajrm/locale/en';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvRecordDetailsPage from '../UI/atoms/OvRecordDetailsPage';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import OvTextFieldFormControl from '../UI/atoms/OvTextFieldFormControl';
import OvSelectFormControl from '../UI/atoms/OvSelectFormControl';
import OvDateTimePicker from '../UI/molecules/OvDateTimePicker';
import OvRecordDetailsErrorState from '../UI/molecules/OvRecordDetailsErrorState';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';
import { MenuItem } from '@mui/material';
import Colours from '../../design-system/colours';
import OvFormObserver from '../UI/atoms/OvFormObserver';
import {
  CreateAdminApiKeyDto,
  UpdateAdminApiKeyDto,
  ApiKeyStatus,
  ApiKeyResponseDto,
} from '../../common/model/dto/api-key';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { useAdminApiKey } from '../../hooks/use-admin-api-key';
import { useAdminApiKeyMutations } from '../../hooks/use-admin-api-key-mutations';
import ApiKeyCreationModal from '../UI/molecules/ApiKeyCreationModal';

const API_KEY_LIST_PATH = '/api-keys';

const ApiKeyCreateEditPage: React.FC = () => {
  const { apiKeyId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createdApiKey, setCreatedApiKey] = useState<ApiKeyResponseDto | null>(
    null
  );
  const [apiKeyFormValue, setApiKeyFormValue] = useState<
    CreateAdminApiKeyDto | UpdateAdminApiKeyDto
  >({
    partner_route: '',
    partner_name: '',
    status: ApiKeyStatus.ACTIVE,
    allowed_routes: [],
    metadata: {},
    expires_at: undefined,
    revocation_reason: undefined,
  });
  const [metadataJson, setMetadataJson] = useState<any>({});

  const {
    data: apiKey,
    isFetching: isApiKeyFetching,
    error: apiKeyError,
    refetch,
  } = useAdminApiKey(apiKeyId);
  const { createApiKey, updateApiKey } = useAdminApiKeyMutations();
  const saveError = createApiKey.error ?? updateApiKey.error;
  const isLoading =
    isApiKeyFetching || createApiKey.isPending || updateApiKey.isPending;

  useEffect(() => {
    if (apiKey) {
      setApiKeyFormValue({
        partner_route: apiKey.partner_route,
        partner_name: apiKey.partner_name,
        status: apiKey.status,
        allowed_routes: apiKey.allowed_routes,
        metadata: apiKey.metadata || {},
        expires_at: apiKey.expires_at,
        revocation_reason: apiKey.revocation_reason,
      });
      setMetadataJson(apiKey.metadata || {});
    }
  }, [apiKey]);

  const saveApiKey = () => {
    const formData = {
      ...apiKeyFormValue,
      metadata: metadataJson,
    };

    if (apiKeyId) {
      updateApiKey.mutate(
        { id: apiKeyId, apiKey: formData as UpdateAdminApiKeyDto },
        { onSuccess: () => navigate(API_KEY_LIST_PATH) }
      );
    } else {
      createApiKey.mutate(formData as CreateAdminApiKeyDto, {
        onSuccess: setCreatedApiKey,
      });
    }
  };

  const handleModalClose = () => {
    setCreatedApiKey(null);
    navigate(API_KEY_LIST_PATH);
  };

  const handleFormValueChange = (
    value: CreateAdminApiKeyDto | UpdateAdminApiKeyDto
  ) => {
    setApiKeyFormValue(value);
  };

  const handleJsonChange = (value: any) => {
    setMetadataJson(value.jsObject);
  };

  const validationSchema = Yup.object().shape({
    partner_route: Yup.string().required(t('common.requiredField')),
    partner_name: Yup.string().required(t('common.requiredField')),
    allowed_routes: Yup.array().of(Yup.string()),
  });

  const pageTitle = t(apiKeyId ? 'apiKeys.update' : 'apiKeys.create');

  if (apiKeyId && !apiKey) {
    return (
      <OvRecordDetailsPage>
        {apiKeyError ? (
          <OvRecordDetailsErrorState
            title={t('apiKeys.details.errorTitle')}
            error={apiKeyError}
            onRetry={() => refetch()}
            backLabel={t('common.apiKeys')}
            backPath={API_KEY_LIST_PATH}
          />
        ) : (
          <OvLoadingIndicator position="fixed" />
        )}
      </OvRecordDetailsPage>
    );
  }

  return (
    <OvRecordDetailsPage aria-busy={isLoading}>
      {saveError && (
        <OvErrorMessage message={getAxiosErrorMessage(saveError)} />
      )}

      <OvRecordDetailsCanvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('apiKeys.details.breadcrumb')}
          backControl={<OvBackButton label={t('common.apiKeys')} />}
          current={apiKey?.partner_name ?? pageTitle}
          title={pageTitle}
          meta={apiKey?.partner_route}
          actions={
            <OvRecordHeaderActions>
              <OvCompactButton onClick={saveApiKey} icon={<AddIcon />}>
                {pageTitle}
              </OvCompactButton>
            </OvRecordHeaderActions>
          }
        />

        <Formik
          initialValues={apiKeyFormValue}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={saveApiKey}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <OvFormObserver valuesChanged={handleFormValueChange} />

              <OvRecordDetailsSection title={t('apiKeys.fields.basicInfo')}>
                <FieldWrapper>
                  <Field
                    name="partner_route"
                    component={OvTextFieldFormControl}
                    label={t('apiKeys.fields.partnerRoute')}
                    placeholder={t('apiKeys.fields.partnerRoutePlaceholder')}
                    fullWidth
                    required
                  />
                </FieldWrapper>

                <FieldWrapper>
                  <Field
                    name="partner_name"
                    component={OvTextFieldFormControl}
                    label={t('apiKeys.fields.partnerName')}
                    placeholder={t('apiKeys.fields.partnerNamePlaceholder')}
                    fullWidth
                    required
                  />
                </FieldWrapper>

                <FieldWrapper>
                  <Field
                    name="status"
                    component={OvSelectFormControl}
                    label={t('apiKeys.fields.status')}
                    fullWidth
                  >
                    <MenuItem value={ApiKeyStatus.ACTIVE}>
                      {t('apiKeys.status.active')}
                    </MenuItem>
                    <MenuItem value={ApiKeyStatus.INACTIVE}>
                      {t('apiKeys.status.inactive')}
                    </MenuItem>
                    <MenuItem value={ApiKeyStatus.REVOKED}>
                      {t('apiKeys.status.revoked')}
                    </MenuItem>
                  </Field>
                </FieldWrapper>

                {values.status === ApiKeyStatus.REVOKED && (
                  <FieldWrapper>
                    <Field
                      name="revocation_reason"
                      component={OvTextFieldFormControl}
                      label={t('apiKeys.fields.revocationReason')}
                      placeholder={t(
                        'apiKeys.fields.revocationReasonPlaceholder'
                      )}
                      fullWidth
                    />
                  </FieldWrapper>
                )}
              </OvRecordDetailsSection>

              <OvRecordDetailsSection title={t('apiKeys.fields.routes')}>
                <FieldWrapper>
                  <Field
                    name="allowed_routes"
                    component={OvTextFieldFormControl}
                    label={t('apiKeys.fields.allowedRoutes')}
                    placeholder={t('apiKeys.fields.allowedRoutesPlaceholder')}
                    fullWidth
                    multiline
                    rows={4}
                    helperText={t('apiKeys.fields.allowedRoutesHelp')}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const routes = e.target.value
                        .split('\n')
                        .filter((route) => route.trim());
                      setFieldValue('allowed_routes', routes);
                    }}
                    value={values.allowed_routes?.join('\n') || ''}
                  />
                </FieldWrapper>
              </OvRecordDetailsSection>

              <OvRecordDetailsSection title={t('apiKeys.fields.advanced')}>
                <FieldWrapper>
                  <OvDateTimePicker
                    label={t('apiKeys.fields.expiresAt')}
                    value={
                      values.expires_at ? new Date(values.expires_at) : null
                    }
                    onChange={(date: Date | null) => {
                      setFieldValue('expires_at', date);
                    }}
                    disablePast
                    views={['year', 'month', 'day', 'hours', 'minutes']}
                  />
                </FieldWrapper>

                <FieldWrapper>
                  <JsonLabel>{t('apiKeys.fields.metadata')}</JsonLabel>
                  <JsonContainer>
                    <JSONInput
                      id="metadata_editor"
                      placeholder={metadataJson}
                      locale={locale}
                      theme="light_mitsuketa_tribute"
                      height="200px"
                      onChange={handleJsonChange}
                    />
                  </JsonContainer>
                  <JsonHelperText>
                    {t('apiKeys.fields.metadataHelp')}
                  </JsonHelperText>
                </FieldWrapper>
              </OvRecordDetailsSection>
            </Form>
          )}
        </Formik>
      </OvRecordDetailsCanvas>

      {isLoading && <OvLoadingIndicator position="fixed" />}

      {createdApiKey && (
        <ApiKeyCreationModal
          open
          onClose={handleModalClose}
          apiKey={createdApiKey.api_key || ''}
          partnerName={createdApiKey.partner_name}
        />
      )}
    </OvRecordDetailsPage>
  );
};

const FieldWrapper = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const JsonLabel = styled.label`
  display: block;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const JsonContainer = styled.div`
  margin-bottom: 0.5rem;
`;

const JsonHelperText = styled.div`
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
  margin-top: 0.25rem;
`;

export default ApiKeyCreateEditPage;
